import React from "react";
import { Link } from "react-router-dom";
import { Phone, MapPin, Check } from "lucide-react";
import { Hero } from "../components/Hero";

const HERO_DESKTOP = "/images/hero/HeroImageHome-Desktop.webp";
const HERO_MOBILE = "/images/hero/HeroImageHome-Mobile.webp";

const features = [
  {
    icon: "/images/secondary/Pizza_asporto.webp",
    title: "Pizza da Asporto",
    text: "Ordina la tua pizza e ritirala in pizzeria velocemente, sempre appena sfornata.",
    testId: "feature-asporto",
  },
  {
    icon: "/images/secondary/IconcinaConsegne.webp",
    title: "Consegna a Domicilio",
    text: "Consegna rapida e affidabile a casa tua, con la stessa qualità di sempre.",
    testId: "feature-domicilio",
  },
  {
    icon: "/images/secondary/IconcinaTorta.webp",
    title: "Torta Livornese",
    text: "La vera torta di ceci secondo la tradizione: farina di ceci, olio evo, sale.",
    testId: "feature-torta",
  },
  {
    icon: "/images/secondary/CinqueCinque_new.webp",
    title: "5 e 5 Livorno",
    text: "Il classico panino con la torta di ceci: semplice, autentico, livornese.",
    testId: "feature-cinque",
  },
];

const reasons = [
  "Impasti a lunga lievitazione",
  "Impasto integrale e ai 7 cereali",
  "Ingredienti freschi e selezionati",
  "Ampia scelta di pizze classiche e speciali",
  "Pizza da asporto veloce",
  "Consegna a domicilio affidabile",
  "Torta Livornese e 5 e 5 della tradizione",
  "Cortesia, professionalità e passione",
];

export default function Home() {
  return (
    <>
      <Hero
        testId="home-hero"
        desktopImage={HERO_DESKTOP}
        mobileImage={HERO_MOBILE}
        alt="Pizza artigianale della Pizzeria Salviano a Livorno"
        eyebrow="Pizzeria Salviano · Livorno"
        title={
          <>
            La tua Pizzeria<br />
            a <span style={{ color: "#f5c518" }}>Livorno</span>
          </>
        }
        subtitle="Pizza da Asporto, Consegna a Domicilio e Specialità Livornesi"
        actions={
          <>
            <Link to="/menu" className="ps-btn ps-btn-primary" data-testid="hero-cta-order">
              Ordina ora
            </Link>
            <a href="tel:+390586326286" className="ps-btn ps-btn-outline" data-testid="hero-cta-call">
              <Phone size={18} /> Chiama
            </a>
          </>
        }
      />

      <section className="py-16 sm:py-24" style={{ background: "var(--ps-cream)" }} data-testid="home-intro">
        <div className="mx-auto max-w-5xl px-4 sm:px-6">
          <div className="text-center mb-10">
            <span className="ornament font-display text-sm sm:text-base uppercase tracking-[0.3em]">La nostra storia</span>
            <h2 className="section-title mt-5 text-3xl sm:text-4xl lg:text-5xl xl:text-6xl">
              Pizzeria Salviano: la tua Pizzeria a Livorno
            </h2>
          </div>
          <div className="font-body text-[var(--ps-ink)] leading-relaxed space-y-6 text-lg sm:text-xl lg:text-[1.35rem]">
            <p>
              Se cerchi una <strong>Pizzeria Livorno</strong> capace di unire qualità, tradizione e servizio,{" "}
              <strong>Pizzeria Salviano</strong> è il posto giusto. Situata nel quartiere Salviano, in{" "}
              <strong>Via Costanza 12/14</strong>, la nostra pizzeria prepara ogni giorno pizze artigianali,
              specialità della cucina livornese e prodotti realizzati con ingredienti selezionati.
            </p>
            <p>
              Da anni rappresentiamo un punto di riferimento tra le <strong>pizzerie Livorno</strong>, grazie a un
              impasto leggero, a una <strong>lunga lievitazione</strong> e alla scelta di materie prime fresche.
              Ogni pizza viene preparata al momento per offrire gusto, fragranza e qualità.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20" style={{ background: "#fbf3e0" }} data-testid="home-features">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((f) => (
              <div key={f.title} className="feature-card text-center" data-testid={f.testId}>
                <img src={f.icon} alt={f.title} className="feature-icon" loading="lazy" decoding="async" />
                <h3 className="font-display uppercase tracking-[0.1em] text-[var(--ps-red)] text-xl lg:text-2xl mb-2">
                  {f.title}
                </h3>
                <p className="text-base lg:text-lg text-[var(--ps-ink-soft)] leading-relaxed">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24" style={{ background: "var(--ps-cream)" }} data-testid="home-artigianali">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className="order-2 lg:order-1">
            <span className="ornament font-display text-sm sm:text-base uppercase tracking-[0.3em]">Qualità artigianale</span>
            <h2 className="section-title mt-5 text-3xl sm:text-4xl lg:text-5xl xl:text-6xl">
              Pizze Artigianali,<br />Ingredienti Freschi,<br />Gusto Autentico
            </h2>
            <p className="mt-6 text-[var(--ps-ink-soft)] text-lg sm:text-xl leading-relaxed">
              Ogni pizza è preparata al momento con <strong>impasti a lunga lievitazione</strong> e materie prime
              selezionate. Dalla Margherita alle speciali, trovi sempre gusto, fragranza e qualità.
            </p>
            <p className="mt-4 text-[var(--ps-ink-soft)] text-lg leading-relaxed">
              La <strong>Pizza da Asporto Livorno</strong> è la scelta ideale per chi desidera gustare una pizza
              appena sfornata ovunque preferisca. Impasto leggero, digeribile e ricco di sapore.
            </p>
            <Link to="/menu" className="ps-btn ps-btn-primary mt-8" data-testid="artigianali-cta">
              Scopri il Menù
            </Link>
          </div>
          <div className="order-1 lg:order-2 relative overflow-hidden rounded-lg shadow-2xl">
            <img
              src="/images/secondary/Impasto.webp"
              alt="Impasto a lunga lievitazione preparato con ingredienti freschi"
              className="w-full h-[320px] sm:h-[440px] lg:h-[540px] object-cover"
              loading="lazy"
              decoding="async"
            />
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20" style={{ background: "#fbf3e0" }} data-testid="home-specialita">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="text-center mb-12">
            <span className="ornament font-display text-sm sm:text-base uppercase tracking-[0.3em]">Livorno nel piatto</span>
            <h2 className="section-title mt-5 text-3xl sm:text-4xl lg:text-5xl xl:text-6xl">
              Le Specialità della Tradizione Livornese
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            <article className="group rounded-lg overflow-hidden bg-white shadow-md hover:shadow-2xl transition-shadow" data-testid="spec-torta">
              <div className="overflow-hidden">
                <img src="/images/secondary/Torta.webp" alt="Torta Livornese - torta di ceci tradizionale"
                  className="w-full h-72 lg:h-96 object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
              </div>
              <div className="p-7 text-center">
                <h3 className="font-display uppercase tracking-[0.08em] text-[var(--ps-red)] text-2xl lg:text-3xl">Torta Livornese</h3>
                <p className="mt-3 text-[var(--ps-ink-soft)] text-lg">
                  La celebre <strong>torta di ceci</strong>, semplice e genuina, simbolo della cucina livornese.
                </p>
              </div>
            </article>
            <article className="group rounded-lg overflow-hidden bg-white shadow-md hover:shadow-2xl transition-shadow" data-testid="spec-cinque">
              <div className="overflow-hidden">
                <img src="/images/secondary/CinqueCinque.webp" alt="5 e 5 Livornese, il classico panino con la torta di ceci"
                  className="w-full h-72 lg:h-96 object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
              </div>
              <div className="p-7 text-center">
                <h3 className="font-display uppercase tracking-[0.08em] text-[var(--ps-red)] text-2xl lg:text-3xl">5 e 5 Livorno</h3>
                <p className="mt-3 text-[var(--ps-ink-soft)] text-lg">
                  Il panino con la <strong>torta di ceci</strong>: una tradizione che non passa mai di moda.
                </p>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section className="relative py-20 sm:py-24" style={{ background: "linear-gradient(135deg,#8b1e1e 0%,#6f1717 100%)", color: "#f4ead8" }} data-testid="home-perche">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="font-display text-sm sm:text-base uppercase tracking-[0.3em] text-[#f5c518]">La scelta giusta</span>
            <h2 className="font-display uppercase font-bold text-3xl sm:text-4xl lg:text-5xl xl:text-6xl mt-5 leading-[1.06]">
              Perché scegliere <br />
              Pizzeria <span style={{ color: "#f5c518" }}>Salviano</span>
            </h2>
            <ul className="mt-8 space-y-3">
              {reasons.map((r) => (
                <li key={r} className="flex items-start gap-3 text-lg sm:text-xl">
                  <span className="mt-1 shrink-0 w-7 h-7 rounded-full bg-[#f5c518] text-[#8b1e1e] flex items-center justify-center">
                    <Check size={16} strokeWidth={3} />
                  </span>
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="hidden lg:block relative">
            <img src="/images/secondary/IconcinaFooterPizza.webp" alt="Pizza Salviano"
              className="w-full max-w-md mx-auto drop-shadow-2xl" loading="lazy" />
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20" style={{ background: "var(--ps-cream)" }} data-testid="home-cta">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-10">
          <div className="rounded-lg p-8 lg:p-10" style={{ background: "#fdf7ea", border: "1px solid rgba(31,23,18,0.08)" }}>
            <MapPin size={32} className="text-[var(--ps-red)]" />
            <h3 className="section-title mt-3 text-2xl lg:text-3xl xl:text-4xl">Vieni a trovarci in Via Costanza 12/14</h3>
            <p className="mt-3 text-[var(--ps-ink-soft)] text-lg">
              Siamo nel <strong>quartiere Salviano</strong>, facilmente raggiungibile da tutta Livorno.
            </p>
            <Link to="/contatti" className="ps-btn ps-btn-primary mt-6" data-testid="cta-visit">
              <MapPin size={16} /> Come raggiungerci
            </Link>
          </div>
          <div className="rounded-lg p-8 lg:p-10" style={{ background: "#fdf7ea", border: "1px solid rgba(31,23,18,0.08)" }}>
            <Phone size={32} className="text-[var(--ps-red)]" />
            <h3 className="section-title mt-3 text-2xl lg:text-3xl xl:text-4xl">Chiama e ordina</h3>
            <p className="mt-3 text-[var(--ps-ink-soft)] text-lg">
              Ordina la tua pizza preferita e gustala dove vuoi: a casa, al lavoro o con chi ami.
            </p>
            <a href="tel:+390586326286" className="ps-btn ps-btn-primary mt-6" data-testid="cta-call">
              <Phone size={16} /> Chiamaci: 0586 326286
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
